import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Typography, Alert, CircularProgress,
  Grid, IconButton, Tooltip, LinearProgress, useMediaQuery, useTheme
} from '@mui/material';
import {
  Refresh, Schedule, CheckCircle, Storage, Backup as BackupIcon
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { GlassmorphicCard, CardTitle, BodyText, SecondaryText, SectionTitle } from '../../styles/sharedStyles';

const StyledCard = styled(GlassmorphicCard)(({ theme }) => ({
  height: '100%',
  '&:hover': {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
  },
}));

const StatusChip = styled('div', {
  shouldForwardProp: (prop) => prop !== 'status'
})(({ status }) => {
  let bgColor, textColor, borderColor;

  const statusStr = String(status || 'unknown').toLowerCase();

  switch (statusStr) {
    case 'running':
    case 'active':
    case 'enabled':
    case 'true':
    case 'idle':
      bgColor = 'rgba(0, 255, 136, 0.1)';
      textColor = '#00ff88';
      borderColor = 'rgba(0, 255, 136, 0.3)';
      break;
    case 'stopped':
    case 'down':
    case 'disabled':
    case 'false':
      bgColor = 'rgba(255, 68, 68, 0.1)';
      textColor = '#ff4444';
      borderColor = 'rgba(255, 68, 68, 0.3)';
      break;
    case 'pending':
    case 'starting':
    case 'stopping':
      bgColor = 'rgba(255, 193, 7, 0.1)';
      textColor = '#ffc107';
      borderColor = 'rgba(255, 193, 7, 0.3)';
      break;
    default:
      bgColor = 'rgba(158, 158, 158, 0.1)';
      textColor = '#9e9e9e';
      borderColor = 'rgba(158, 158, 158, 0.3)';
  }

  return {
    background: bgColor,
    color: textColor,
    border: `1px solid ${borderColor}`,
    fontWeight: 600,
    padding: '8px 16px',
    borderRadius: '20px',
    display: 'inline-block',
    fontSize: '0.95rem',
    textTransform: 'capitalize',
    letterSpacing: '0.5px',
  };
});

const HABackups = ({ apiBaseUrl, token }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [haBackupSensors, setHaBackupSensors] = useState({});
  const [haInfo, setHaInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBackupData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [haBackupsResponse, haInfoResponse] = await Promise.all([
        axios.get(`${apiBaseUrl}/api/homeassistant/backups`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${apiBaseUrl}/api/homeassistant/info`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);
      setHaBackupSensors(haBackupsResponse.data || {});
      setHaInfo(haInfoResponse.data || null);
    } catch (err) {
      console.error('Error fetching Home Assistant backup data:', err);
      setError(err.response?.data?.detail || 'Failed to fetch Home Assistant backup data');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchBackupData();
    // Auto-refresh every 5 minutes (300000 ms) to avoid rate limiting
    const interval = setInterval(fetchBackupData, 300000);
    return () => clearInterval(interval);
  }, [fetchBackupData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const now = new Date();
    const diffMs = now - date;
    const absDiffMs = Math.abs(diffMs);
    const isFuture = diffMs < 0;

    const diffMins = Math.floor(absDiffMs / 60000);
    const diffHours = Math.floor(absDiffMs / 3600000);
    const diffDays = Math.floor(absDiffMs / 86400000);

    if (absDiffMs < 60000) return 'Just now';

    if (isFuture) {
      if (diffMins < 60) return `In ${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
      if (diffHours < 24) return `In ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
      if (diffDays < 7) return `In ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else {
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDiskSpace = (value, unit) => {
    if (!value) return 'Unknown';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;

    // If unit is MB, convert to GB for better readability
    if (unit === 'MB') {
      const gbValue = numValue / 1024;
      return `${gbValue.toFixed(2)} GB`;
    }

    // If unit is GB, format with 2 decimal places
    if (unit === 'GB') {
      return `${numValue.toFixed(2)} GB`;
    }

    // Default: return as-is with unit
    return `${numValue} ${unit || ''}`;
  };

  if (loading && !haBackupSensors.manager_state && !haInfo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <SectionTitle sx={{ fontSize: isMobile ? '0.9rem' : '1.1rem' }}>
          Data Protection
        </SectionTitle>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchBackupData} disabled={loading} sx={{ color: '#00d4ff' }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      {haInfo && (
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(3, 155, 229, 0.1)', borderRadius: '12px', border: '1px solid rgba(3, 155, 229, 0.3)' }}>
          <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
            {haInfo.location_name || 'Home Assistant'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Version: {haInfo.version || 'Unknown'}
          </Typography>
        </Box>
      )}

      {!haBackupSensors.manager_state ? (
        <Box sx={{
          textAlign: 'center',
          py: 6,
          color: 'rgba(255, 255, 255, 0.7)'
        }}>
          <Typography variant="body1">
            {loading ? 'Loading Home Assistant backup sensors...' : 'No Home Assistant backup sensors found'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Manager State Card */}
          {haBackupSensors.manager_state && (
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <BackupIcon sx={{ color: '#039be5', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1rem' }}>
                      Backup Manager
                    </Typography>
                  </Box>
                  <StatusChip
                    status={haBackupSensors.manager_state.state === 'idle' ? 'active' : 'running'}
                    sx={{ mb: 2 }}
                  >
                    {haBackupSensors.manager_state.state || 'Unknown'}
                  </StatusChip>
                  {haBackupSensors.manager_state.last_updated && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
                      Last updated: {formatDate(haBackupSensors.manager_state.last_updated)}
                    </Typography>
                  )}
                </CardContent>
              </StyledCard>
            </Grid>
          )}

          {/* Next Scheduled Backup Card */}
          {haBackupSensors.next_scheduled && (
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Schedule sx={{ color: '#00d4ff', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1rem' }}>
                      Next Scheduled Backup
                    </Typography>
                  </Box>
                  <BodyText sx={{ fontSize: '1.125rem', mb: 0.5 }}>
                    {formatDate(haBackupSensors.next_scheduled.state) || 'Not scheduled'}
                  </BodyText>
                  {haBackupSensors.next_scheduled.state && !isNaN(new Date(haBackupSensors.next_scheduled.state).getTime()) && (
                    <SecondaryText sx={{ fontSize: '0.85rem', mb: 1.5, color: 'rgba(255,255,255,0.6)' }}>
                      {new Date(haBackupSensors.next_scheduled.state).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </SecondaryText>
                  )}
                  {haBackupSensors.next_scheduled.attributes && (
                    <>
                      {haBackupSensors.next_scheduled.attributes.friendly_name && (
                        <SecondaryText sx={{ fontSize: '0.75rem' }}>
                          {haBackupSensors.next_scheduled.attributes.friendly_name}
                        </SecondaryText>
                      )}
                    </>
                  )}
                </CardContent>
              </StyledCard>
            </Grid>
          )}

          {/* Last Successful Backup Card */}
          {haBackupSensors.last_successful && (
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CheckCircle sx={{ color: '#00ff88', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1rem' }}>
                      Last Successful Backup
                    </Typography>
                  </Box>
                  <BodyText sx={{ fontSize: '1.125rem', mb: 1 }}>
                    {formatDate(haBackupSensors.last_successful.state) || 'Never'}
                  </BodyText>
                  {haBackupSensors.last_successful.attributes && (
                    <>
                      {haBackupSensors.last_successful.attributes.friendly_name && (
                        <SecondaryText sx={{ fontSize: '0.75rem' }}>
                          {haBackupSensors.last_successful.attributes.friendly_name}
                        </SecondaryText>
                      )}
                    </>
                  )}
                </CardContent>
              </StyledCard>
            </Grid>
          )}

          {/* Free Disk Space Card */}
          {haBackupSensors.free_disk_space && (
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Storage sx={{ color: '#ffc107', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1rem' }}>
                      Free Disk Space (Backup)
                    </Typography>
                  </Box>
                  <BodyText sx={{ fontSize: '1.125rem', mb: 1 }}>
                    {formatDiskSpace(
                      haBackupSensors.free_disk_space.state,
                      haBackupSensors.free_disk_space.attributes?.unit_of_measurement
                    )}
                  </BodyText>
                  {(() => {
                    const freeRaw = parseFloat(haBackupSensors.free_disk_space.state);
                    const unit = haBackupSensors.free_disk_space.attributes?.unit_of_measurement;
                    let freeGB = freeRaw;
                    if (unit === 'MB') freeGB = freeRaw / 1024;
                    
                    const totalGB = 234;
                    const usedGB = totalGB - freeGB;
                    const usedPercent = Math.min(100, Math.max(0, (usedGB / totalGB) * 100));
                    
                    return (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <SecondaryText sx={{ fontSize: '0.75rem' }}>Usage</SecondaryText>
                          <SecondaryText sx={{ fontSize: '0.75rem' }}>{usedPercent.toFixed(1)}%</SecondaryText>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={usedPercent} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: usedPercent > 90 ? '#ff4444' : usedPercent > 70 ? '#ffc107' : '#00ff88',
                              borderRadius: 4,
                            }
                          }} 
                        />
                        <SecondaryText sx={{ fontSize: '0.7rem', mt: 0.5, textAlign: 'right', display: 'block' }}>
                          Total: {totalGB} GB
                        </SecondaryText>
                      </Box>
                    );
                  })()}
                </CardContent>
              </StyledCard>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default HABackups;
